import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { assessmentSessions } from '../api/client';
import QuestionRenderer from '../components/assessment/QuestionRenderer';

export default function AssessmentSessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    loadSession();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [id]);

  const loadSession = async () => {
    try {
      const response = await assessmentSessions.get(id);
      const data = response.data;
      setSession(data);

      // Initialize responses from existing answers
      const existingResponses = {};
      data.sections?.forEach((section) => {
        section.questions?.forEach((question) => {
          if (question.answer !== null && question.answer !== undefined) {
            existingResponses[question.id] = question.answer;
          }
        });
      });
      setResponses(existingResponses);

      // Set current section based on saved position
      if (data.current_section_id) {
        const sectionIdx = data.sections?.findIndex((s) => s.id === data.current_section_id);
        if (sectionIdx >= 0) {
          setCurrentSectionIndex(sectionIdx);
        }
      }
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  // Debounced auto-save
  const scheduleAutoSave = useCallback(
    (newResponses, sectionId) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveResponses(newResponses, sectionId);
      }, 1500);
    },
    [id]
  );

  const saveResponses = async (responsesToSave, sectionId = null) => {
    setSaving(true);
    try {
      const responseData = Object.entries(responsesToSave).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer: answer,
      }));

      await assessmentSessions.update(id, {
        responses: responseData,
        current_section_id: sectionId,
      });
      setLastSaved(new Date());
    } catch (err) {
      console.error('Failed to save responses:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    const newResponses = { ...responses, [questionId]: value };
    setResponses(newResponses);
    scheduleAutoSave(newResponses, session.sections[currentSectionIndex].id);
  };

  const handleNextSection = async () => {
    // Save current progress before moving
    await saveResponses(responses, session.sections[currentSectionIndex + 1]?.id);
    setCurrentSectionIndex((prev) => Math.min(prev + 1, session.sections.length - 1));
    window.scrollTo(0, 0);
  };

  const handlePrevSection = async () => {
    await saveResponses(responses, session.sections[currentSectionIndex - 1]?.id);
    setCurrentSectionIndex((prev) => Math.max(prev - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSectionClick = async (index) => {
    await saveResponses(responses, session.sections[index]?.id);
    setCurrentSectionIndex(index);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!confirm('Are you sure you want to submit this assessment? You cannot make changes after submission.')) {
      return;
    }

    setSubmitting(true);
    try {
      // Save any pending responses first
      await saveResponses(responses);
      // Then submit
      await assessmentSessions.submit(id);
      navigate(`/app/assessments/${id}/results`);
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      setError(err.response?.data?.error || 'Failed to submit assessment');
      setSubmitting(false);
    }
  };

  const getSectionProgress = (section) => {
    const totalQuestions = section.questions?.length || 0;
    const answeredQuestions = section.questions?.filter((q) => responses[q.id] !== undefined).length || 0;
    return { answered: answeredQuestions, total: totalQuestions };
  };

  const getOverallProgress = () => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    session?.sections?.forEach((section) => {
      totalQuestions += section.questions?.length || 0;
      section.questions?.forEach((q) => {
        if (responses[q.id] !== undefined) {
          answeredQuestions++;
        }
      });
    });
    return { answered: answeredQuestions, total: totalQuestions };
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading assessment...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-red-500">Assessment not found</div>
      </div>
    );
  }

  if (session.status === 'completed') {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-medium text-green-800">Assessment Completed</h2>
          <p className="mt-2 text-green-600">This assessment has already been submitted.</p>
          <Link
            to={`/app/assessments/${id}/results`}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            View Results
          </Link>
        </div>
      </div>
    );
  }

  const currentSection = session.sections?.[currentSectionIndex];
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === session.sections.length - 1;
  const overallProgress = getOverallProgress();
  const progressPercentage = overallProgress.total > 0
    ? Math.round((overallProgress.answered / overallProgress.total) * 100)
    : 0;

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <Link
            to={`/app/students/${session.student_id}/assessments`}
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            &larr; Exit Assessment
          </Link>
          <div className="text-sm text-gray-500">
            {saving ? (
              <span className="text-yellow-600">Saving...</span>
            ) : lastSaved ? (
              <span className="text-green-600">Saved</span>
            ) : null}
          </div>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">{session.template.name}</h1>
        <p className="mt-1 text-sm text-gray-500">{session.template.description}</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Overall Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Overall Progress</span>
          <span>
            {overallProgress.answered} of {overallProgress.total} questions ({progressPercentage}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Section Navigation */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-2 pb-2">
          {session.sections?.map((section, index) => {
            const progress = getSectionProgress(section);
            const isComplete = progress.answered === progress.total;
            const isCurrent = index === currentSectionIndex;
            return (
              <button
                key={section.id}
                onClick={() => handleSectionClick(index)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isCurrent
                    ? 'bg-indigo-600 text-white'
                    : isComplete
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span>{section.name}</span>
                <span className="ml-2 opacity-75">
                  {progress.answered}/{progress.total}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Section Content */}
      {currentSection && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{currentSection.name}</h2>
            {currentSection.description && (
              <p className="mt-1 text-sm text-gray-500">{currentSection.description}</p>
            )}
          </div>

          <div className="space-y-8">
            {currentSection.questions?.map((question, index) => (
              <div key={question.id} className="border-b border-gray-200 pb-6 last:border-0">
                <div className="text-xs text-gray-400 mb-2">
                  Question {index + 1} of {currentSection.questions.length}
                </div>
                <QuestionRenderer
                  question={question}
                  value={responses[question.id]}
                  onChange={(value) => handleResponseChange(question.id, value)}
                />
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between items-center border-t border-gray-200 pt-6">
            <button
              onClick={handlePrevSection}
              disabled={isFirstSection}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &larr; Previous Section
            </button>

            <div className="flex space-x-3">
              {isLastSection ? (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Assessment'}
                </button>
              ) : (
                <button
                  onClick={handleNextSection}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Next Section &rarr;
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
